import { useLazyLoadQuery, useMutation } from 'react-relay';

import {
  simpleAuthConfirmRequestTokenMutation,
  simpleAuthConfirmRequestTokenMutation$variables
} from '@/__generated__/simpleAuthConfirmRequestTokenMutation.graphql';
import { simpleAuthRequestRefundTokenForLookupMutation } from '@/__generated__/simpleAuthRequestRefundTokenForLookupMutation.graphql';
import { CorpTypeEnum, LoginType, simpleAuthRequestRefundTokenMutation } from '@/__generated__/simpleAuthRequestRefundTokenMutation.graphql';
import { simpleAuthRequestTokenMutation } from '@/__generated__/simpleAuthRequestTokenMutation.graphql';
import { userInfoQuery } from '@/__generated__/userInfoQuery.graphql';
import {
  requestRefundTokenForLookup as hometaxRefundTokenLookupMutation,
  requestRefundToken as hometaxRefundTokenMutation,
  requestSimpleAuthConfirmToken as simpleAuthConfirmMutation,
  requestSimpleAuthToken as simpleAuthInputMutation
} from '@/graphql/mutation/simple-auth/simple-auth';
import { bznavUserInfo } from '@/graphql/query/simple-auth/user-info';
import { SimpleAuthFromType } from '@/lib/types/simple-auth';

export const useSimpleAuthUserInfo = () => {
  const userData = useLazyLoadQuery<userInfoQuery>(bznavUserInfo, {});
  const user = userData?.bznavRefund?.user;

  if (!user) {
    return undefined;
  }
  return {
    name: (user?.name === '고객' ? '' : user?.name) ?? '',
    phone: user?.phone ?? '',
    birthday: user?.birthDay ?? '',
    tin: user?.indis?.[0]?.tin ?? ''
  };
};

// input
// 해당 return 값은 requestSimpleAuthConfirmToken 에서 사용
export const useRequestSimpleAuthToken = () => {
  const [requestSimpleAuthToken, isLoading] = useMutation<simpleAuthRequestTokenMutation>(simpleAuthInputMutation);

  return { requestSimpleAuthToken, isLoading };
};

// confirm
// 해당 return 값의 token은 requestRefundToken 에서 사용
export const useRequestSimpleAuthConfirmToken = () => {
  const [commit, isLoading] = useMutation<simpleAuthConfirmRequestTokenMutation>(simpleAuthConfirmMutation);

  const requestSimpleAuthConfirmToken = (variables: simpleAuthConfirmRequestTokenMutation$variables) => {
    return new Promise((resolve, reject) =>
      commit({
        variables,
        onCompleted: (response, serverErrors) => {
          const { token, errors, result } = response.hometaxSimpleAuthConfirm ?? {};
          if (serverErrors) {
            console.info('requestSimpleAuthConfirmToken onCompleted serverErrors');
            reject({ error: { code: 'SERVER_ERROR' }, data: undefined });
          }
          if (errors || !result || !token) {
            console.info('requestSimpleAuthConfirmToken onCompleted errors || !result');
            reject({ error: errors, data: { simpleAuthConfirmToken: token } });
          }
          resolve(token);
        },
        onError: (error) => {
          console.info('requestSimpleAuthConfirmToken onError');
          reject({ error, data: undefined });
        }
      })
    );
  };
  return { requestSimpleAuthConfirmToken, isLoading };
};

type RejectType = {
  error: unknown;
  data?: unknown;
};
export const useRequestHometaxRefundToken = (fromType: SimpleAuthFromType) => {
  const lookup = useMutation<simpleAuthRequestRefundTokenForLookupMutation>(hometaxRefundTokenLookupMutation);
  const search = useMutation<simpleAuthRequestRefundTokenMutation>(hometaxRefundTokenMutation);

  const commit = (fromType === 'lookup' ? lookup : search)[0];
  const isLoading = (fromType === 'lookup' ? lookup : search)[1];

  const requestRefundToken = (variables: { corpType: CorpTypeEnum; loginType: LoginType; token: string }): Promise<string> => {
    return new Promise<string>((resolve: (value: string) => void, reject: (reason: RejectType) => void) =>
      commit({
        variables,
        onCompleted: (response, serverErrors) => {
          const { result, errors, ...data } = response.requestRefundTokenMutation ?? {};
          if (serverErrors) {
            console.info('requestRefundToken onCompleted serverErrors');
            reject({ error: { code: 'SERVER_ERROR' }, data: undefined });
          }
          if (errors || !result || !data.refundToken) {
            console.info('requestRefundToken onCompleted errors || !result');
            reject({ error: errors, data: { requestRefundToken: data } });
          }
          resolve(data.refundToken ?? '');
        },
        onError: (error) => {
          console.info('requestRefundToken onError');
          reject({ error, data: undefined });
        }
      })
    );
  };

  return { requestRefundToken, isLoading };
};
